import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { Loader2, Search, Camera, CameraOff, Check, UserCheck, AlertTriangle, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CollectTab({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [code, setCode] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const lookup = async (ref) => {
    const clean = (ref || "").trim().toUpperCase();
    if (!clean) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings/${clean}`, auth);
      setBooking(data);
    } catch (err) {
      setBooking(null);
      toast.error(err?.response?.data?.detail || "No booking found.");
    } finally {
      setLoading(false);
    }
  };

  const checkin = async (count, override = false) => {
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${booking.booking_ref}/checkin`, { count, override }, auth);
      setBooking((b) => ({ ...b, checked_in_count: data.checked_in_count }));
      if (data.fully_collected) toast.success("All guests checked in — ticket is now used.");
      else toast.success(`${data.checked_in_count} of ${data.quantity} guests checked in.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not check in.");
    }
  };

  const accept = async (override = false) => {
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${booking.booking_ref}/accept`, { override }, auth);
      setBooking((b) => ({ ...b, checked_in_count: data.quantity, accepted: true }));
      toast.success("Ticket accepted — it cannot be used again.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept ticket.");
    }
  };

  const stopScan = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => {
    if (!scanning) return;
    let cancelled = false;
    const start = async () => {
      try {
        const html5 = new Html5Qrcode("qr-reader");
        scannerRef.current = html5;
        await html5.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
          async (decoded) => {
            if (cancelled) return;
            await stopScan();
            setCode(decoded);
            lookup(decoded);
          }, () => {});
      } catch (err) {
        toast.error("Could not open camera. Check permissions or use manual entry.");
        setScanning(false);
      }
    };
    start();
    return () => { cancelled = true; if (scannerRef.current) { scannerRef.current.stop().catch(() => {}); scannerRef.current = null; } };
  }, [scanning]);

  const fully = booking && booking.checked_in_count >= booking.quantity;
  const paid = booking && booking.payment_status === "paid";
  const dateMismatch = booking && booking.date_matches_today === false;

  return (
    <div className="max-w-xl mx-auto" data-testid="collect-tab">
      <div className="ornate-frame bg-[var(--surface)] p-8">
        <h2 className="font-display text-3xl text-[var(--cream)] mb-1">Collect tickets</h2>
        <p className="font-body text-sm text-[var(--muted)] mb-6">Scan the QR code on the guest's ticket, or type their booking reference.</p>

        <div className="flex gap-3">
          <input
            data-testid="collect-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookup(code)}
            placeholder="e.g. BEX-4L5UGE"
            className="field-input flex-1 uppercase"
          />
          <button data-testid="collect-lookup-btn" onClick={() => lookup(code)} disabled={loading}
            className="rounded-full bg-[var(--gold)] text-[var(--bg)] px-6 flex items-center gap-2 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[var(--cream)] transition-colors duration-200 disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Find
          </button>
        </div>

        <div className="mt-4">
          {!scanning ? (
            <button data-testid="collect-scan-btn" onClick={() => setScanning(true)}
              className="w-full border border-[rgba(201,162,74,0.4)] text-[var(--cream)] py-3 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] hover:bg-[rgba(201,162,74,0.12)] transition-colors duration-200">
              <Camera className="w-4 h-4" /> Scan QR with camera
            </button>
          ) : (
            <div>
              <div id="qr-reader" className="w-full overflow-hidden rounded border border-[rgba(201,162,74,0.4)]" />
              <button data-testid="collect-stop-scan-btn" onClick={stopScan}
                className="w-full mt-3 border border-[rgba(201,162,74,0.4)] text-[var(--cream)] py-3 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] hover:bg-[rgba(201,162,74,0.12)] transition-colors duration-200">
                <CameraOff className="w-4 h-4" /> Stop camera
              </button>
            </div>
          )}
        </div>
      </div>

      {booking && (
        <div className={`ornate-frame bg-[var(--surface)] p-8 mt-6 ${fully ? "opacity-90" : ""}`} data-testid="collect-result">
          <div className="flex items-center justify-between">
            <span className="font-display text-3xl text-[var(--gold)] tracking-wide" data-testid="collect-result-ref">{booking.booking_ref}</span>
            {fully ? (
              <span data-testid="collect-status-used" className="px-3 py-1 text-[10px] uppercase tracking-[0.15em] border border-[rgba(157,179,162,0.5)] text-[var(--muted)]">Used · no longer valid</span>
            ) : !paid ? (
              <span className="px-3 py-1 text-[10px] uppercase tracking-[0.15em] border border-red-400 text-red-300">Not paid</span>
            ) : dateMismatch ? (
              <span data-testid="collect-status-wrongdate" className="px-3 py-1 text-[10px] uppercase tracking-[0.15em] border border-amber-400 text-amber-300">Wrong date</span>
            ) : (
              <span data-testid="collect-status-valid" className="px-3 py-1 text-[10px] uppercase tracking-[0.15em] border border-[var(--gold)] text-[var(--gold)]">Valid today</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6 font-body text-sm">
            <div><p className="uppercase tracking-[0.15em] text-[10px] text-[var(--muted)]">Date</p><p className="text-[var(--cream)] mt-1">{booking.date_label}</p></div>
            <div><p className="uppercase tracking-[0.15em] text-[10px] text-[var(--muted)]">Email</p><p className="text-[var(--cream)] mt-1 break-all">{booking.email}</p></div>
            <div><p className="uppercase tracking-[0.15em] text-[10px] text-[var(--muted)]">Party size</p><p className="text-[var(--cream)] mt-1">{booking.quantity} {booking.quantity === 1 ? "person" : "people"}</p></div>
            <div><p className="uppercase tracking-[0.15em] text-[10px] text-[var(--muted)]">Allergies</p><p className="text-[var(--cream)] mt-1">{booking.allergies || "None noted"}</p></div>
          </div>

          <div className="mt-6 border-t border-[rgba(201,162,74,0.2)] pt-6 text-center">
            <p className="uppercase tracking-[0.2em] text-[10px] text-[var(--muted)]">Checked in</p>
            <p className="font-display text-5xl text-[var(--cream)] mt-2" data-testid="collect-progress">
              <span className="text-[var(--gold)]">{booking.checked_in_count}</span> / {booking.quantity}
            </p>
          </div>

          {fully && (
            <p className="mt-6 text-center font-serif-alt text-lg text-[var(--muted)]">This ticket has been used and cannot be accepted again.</p>
          )}

          {paid && !fully && dateMismatch && (
            <div className="mt-6" data-testid="collect-wrongdate-block">
              <div className="flex items-start gap-3 border border-amber-400/50 bg-amber-400/10 px-4 py-4">
                <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
                <p className="font-body text-sm text-amber-100">
                  This ticket is for <span className="font-semibold">{booking.date_label}</span>, not today. It should be rejected.
                </p>
              </div>
              <button data-testid="collect-override-btn" onClick={() => accept(true)}
                className="w-full mt-4 rounded-full border border-amber-400 text-amber-200 py-3 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] hover:bg-amber-400/10 transition-colors duration-200">
                <ShieldAlert className="w-4 h-4" /> Override &amp; accept anyway
              </button>
            </div>
          )}

          {paid && !fully && !dateMismatch && (
            <div className="mt-6 space-y-3" data-testid="collect-accept-block">
              <button data-testid="collect-accept-btn" onClick={() => accept(false)}
                className="w-full rounded-full bg-[var(--gold)] text-[var(--bg)] py-4 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[var(--cream)] transition-colors duration-200">
                <Check className="w-4 h-4" /> Accept ticket ({booking.quantity} {booking.quantity === 1 ? "guest" : "guests"})
              </button>
              <button data-testid="checkin-one-btn" onClick={() => checkin(1)}
                className="w-full rounded-full border border-[var(--gold)] text-[var(--gold)] py-3 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] hover:bg-[rgba(201,162,74,0.12)] transition-colors duration-200">
                <UserCheck className="w-4 h-4" /> Check in 1 guest at a time
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
